/**
 * Date Range Selector Component
 *
 * Provides preset date range buttons and custom date picker.
 * Features:
 * - Preset buttons (This Month, Last Month, QTD, YTD, FY, Custom)
 * - Custom date range picker with native HTML date inputs
 * - Responsive button group
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import type { DateRangePreset } from '../../types/financialInsights';

interface DateRangeSelectorProps {
  /** Currently selected preset */
  selectedPreset: DateRangePreset;
  /** Custom start date (if preset is 'custom') */
  startDate?: string;
  /** Custom end date (if preset is 'custom') */
  endDate?: string;
  /** Callback when date range changes */
  onChange: (
    preset: DateRangePreset,
    startDate?: string,
    endDate?: string
  ) => void;
}

/**
 * Date Range Selector component for filtering dashboard data
 */
export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  selectedPreset,
  startDate,
  endDate,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Update local state when props change (e.g., when parent pre-populates custom dates)
  const [customStartDate, setCustomStartDate] = useState<string>(
    startDate || ''
  );
  const [customEndDate, setCustomEndDate] = useState<string>(endDate || '');

  // Sync local state with props when they change
  React.useEffect(() => {
    if (startDate !== undefined) {
      setCustomStartDate(startDate);
    }
    if (endDate !== undefined) {
      setCustomEndDate(endDate);
    }
  }, [startDate, endDate]);

  /**
   * Preset button configurations with tooltips
   */
  const presets: Array<{
    value: DateRangePreset;
    label: string;
    tooltip?: string;
  }> = [
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'qtd', label: 'QTD', tooltip: 'Quarter to Date' },
    { value: 'ytd', label: 'YTD', tooltip: 'Year to Date' },
    { value: 'fy', label: 'FY', tooltip: 'Fiscal Year' },
    { value: 'custom', label: 'Custom' },
  ];

  /**
   * Handle preset button click
   */
  const handlePresetClick = (preset: DateRangePreset) => {
    // Emit preset change (parent will handle pre-populating custom dates if needed)
    onChange(preset);
  };

  /**
   * Handle custom start date change
   */
  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStartDate = event.target.value;
    setCustomStartDate(newStartDate);

    // Only emit change if both dates are selected
    if (newStartDate && customEndDate) {
      onChange('custom', newStartDate, customEndDate);
    }
  };

  /**
   * Handle custom end date change
   */
  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = event.target.value;
    setCustomEndDate(newEndDate);

    // Only emit change if both dates are selected
    if (customStartDate && newEndDate) {
      onChange('custom', customStartDate, newEndDate);
    }
  };

  return (
    <Stack spacing={2}>
      {/* Preset Buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 1,
        }}
      >
        {isMobile ? (
          // Stack buttons vertically on mobile
          presets.map(preset =>
            preset.tooltip ? (
              <Tooltip key={preset.value} title={preset.tooltip} arrow>
                <Button
                  variant={
                    selectedPreset === preset.value ? 'contained' : 'outlined'
                  }
                  onClick={() => handlePresetClick(preset.value)}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: selectedPreset === preset.value ? 600 : 400,
                  }}
                >
                  {preset.label}
                </Button>
              </Tooltip>
            ) : (
              <Button
                key={preset.value}
                variant={
                  selectedPreset === preset.value ? 'contained' : 'outlined'
                }
                onClick={() => handlePresetClick(preset.value)}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: selectedPreset === preset.value ? 600 : 400,
                }}
              >
                {preset.label}
              </Button>
            )
          )
        ) : (
          // Button group on desktop
          <ButtonGroup variant="outlined" size="small">
            {presets.map(preset =>
              preset.tooltip ? (
                <Tooltip key={preset.value} title={preset.tooltip} arrow>
                  <Button
                    variant={
                      selectedPreset === preset.value ? 'contained' : 'outlined'
                    }
                    onClick={() => handlePresetClick(preset.value)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: selectedPreset === preset.value ? 600 : 400,
                      minWidth: { sm: 80, md: 100 },
                    }}
                  >
                    {preset.label}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  key={preset.value}
                  variant={
                    selectedPreset === preset.value ? 'contained' : 'outlined'
                  }
                  onClick={() => handlePresetClick(preset.value)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: selectedPreset === preset.value ? 600 : 400,
                    minWidth: { sm: 80, md: 100 },
                  }}
                >
                  {preset.label}
                </Button>
              )
            )}
          </ButtonGroup>
        )}
      </Box>

      {/* Custom Date Pickers (only show when custom is selected) */}
      {selectedPreset === 'custom' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={customStartDate}
            onChange={handleStartDateChange}
            size="small"
            fullWidth={isMobile}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: isMobile ? undefined : 200 }}
          />
          <TextField
            label="End Date"
            type="date"
            value={customEndDate}
            onChange={handleEndDateChange}
            size="small"
            fullWidth={isMobile}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: customStartDate || undefined,
            }}
            sx={{ minWidth: isMobile ? undefined : 200 }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default DateRangeSelector;
